import React, { useState } from "react";
import { Phone, MessageCircle } from "lucide-react";
import { WhatsAppShareOptionsModal } from "../../../organisms/Dashboard/whatsappShareModalShared";
import UnderLine from "../../Login/UnderLine";
import TelegramPNG from "../../../../assets/Icons/ShareCode/telegram.png";
import FacebookPNG from "../../../../assets/Icons/ShareCode/facebook.png";
import CopyIconPNG from "../../../../assets/Icons/ShareCode/copy.svg";
import ShareCodePNG from "../../../../assets/Icons/ShareCode/share.png";
import WhatsAppIconPNG from "../../../../assets/Icons/Whatsapp.svg";
import { showSuccessToast } from "../../Utlis/Toast";
import LoadingDots from "../../Utlis/LoadinDots";
import apiCall from "../../../../Redux/ApiCalls/AuthorizedApi";
import { trackClarityEvent } from "../../../../analytics/ClarityAnalytics";
import { CLARITY_EVENTS } from "../../../../analytics/EventTypes";
import { useAppSelector } from "../../../../Redux/Hooks";

interface ShareButtonProps {
  shareUrl?: string;
  onShare?: () => Promise<any>;
  code?: string;
}

const THEME_GRADIENT = "background-linear";

const ShareButton: React.FC<ShareButtonProps> = ({ shareUrl, onShare, code }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [multiModalOpen, setMultiModalOpen] = useState(false);
  const [whatsappOptionsOpen, setWhatsappOptionsOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [phoneInputs, setPhoneInputs] = useState([""]);
  const [imageUrl, setImageUrl] = useState()
  const { currentAlbum } = useAppSelector((state) => state.album)

  /* COPY LINK */
  const handleCopy = async () => {
    const text = onShare ? await onShare() : shareUrl;
    navigator.clipboard.writeText(text || "").then(() => {
      showSuccessToast("Url copied to clipboard.")
    });
    trackClarityEvent(CLARITY_EVENTS.ALBUM_CODE_SHARED, {
      action: "copy",
      share_method: "link",
    });
  };

  /* NORMAL SHARE */
  const handleShare = async (platform: string) => {
    let finalText: any = shareUrl;
    setLoading(true);

    try {
      if (onShare) finalText = await onShare();
      setLoading(false);

      let url = "";
      switch (platform) {
        case "whatsapp":
          url = `https://wa.me/?text=${encodeURIComponent(finalText.message || "")}`;
          break;
        case "facebook":
          url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            typeof finalText === "string" ? finalText : (finalText as any)?.url || ""
          )}`;
          break;
        case "telegram":
          url = `https://t.me/share/url?url=${encodeURIComponent(
            finalText || ""
          )}`;
          break;
      }
      if (url) {
        window.open(url, "_blank");
        trackClarityEvent(CLARITY_EVENTS.ALBUM_CODE_SHARED, {
          action: "share",
          share_method: platform,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  /* MULTI SEND */
  const handleSendToMultiple = async () => {
    try {
      setLoading(true);
      let message;
      if (onShare) {
        message = await onShare();
      }

      const imageUrl = message?.url || (shareUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(shareUrl)}` : "");
      const shareCode = code || shareUrl;
      const albumName = currentAlbum?.name;

      const response = await apiCall({
        method: "POST",
        url: "/project/api/send-whatsapp-message/",
        data: {
          to: phoneInputs.filter(num => num.trim()).map(num => `+91${num}`), // Only send valid numbers
          header_image: imageUrl,
          body_1: shareCode,
          body_2: albumName
        }
      });

      showSuccessToast("Message sent successfully.")
      trackClarityEvent(CLARITY_EVENTS.ALBUM_CODE_SHARED, {
        action: "share",
        share_method: "whatsapp_multiple",
        recipient_count: phoneInputs.filter(num => num.trim()).length,
      });
      setLoading(false)
      setMultiModalOpen(false);
    } catch (error) {
      setLoading(false)
      console.error('Error sending WhatsApp messages:', error);
    }
  };

  /* ADD INPUT FIELD */
  const addPhoneInput = () => setPhoneInputs([...phoneInputs, ""]);

  /* REMOVE FIELD */
  const removePhoneInput = (index: number) => {
    if (phoneInputs.length === 1) return;
    setPhoneInputs(phoneInputs.filter((_, i) => i !== index));
  };

  /* UPDATE INPUT */
  const updatePhone = (index: number, value: string) => {
    const list = [...phoneInputs];
    list[index] = value;
    setPhoneInputs(list);
  };

  /* DIRECT WHATSAPP OPEN */
  const handleDirectWhatsAppOpen = async () => {
    try {
      setLoading(true);
      let shareData: any = shareUrl;

      if (onShare) {
        shareData = await onShare();
      }

      // Create message with QR image URL and code information
      // If onShare provided a message, use it (it already includes QR URL, code, and link)
      // Otherwise, construct a message from available data
      let message = "";
      if (shareData?.message) {
        // Use the message from onShare (it already includes QR URL, code, and link)
        // Just trim leading/trailing whitespace but preserve line breaks
        message = shareData.message.trim();
      } else if (shareData?.url) {
        // Construct message with QR URL and code
        message = `QR Image: ${shareData.url}${code ? `\nCode: ${code}` : ""}${shareUrl ? `\n${shareUrl}` : ""}`;
      } else {
        // Fallback to basic message
        message = `${code ? `Code: ${code}\n` : ""}${shareUrl || ""}`;
      }

      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");

      trackClarityEvent(CLARITY_EVENTS.ALBUM_CODE_SHARED, {
        action: "share",
        share_method: "whatsapp_direct",
      });

      setWhatsappOptionsOpen(false);
      setIsOpen(false);
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* MAIN SHARE BUTTON */}
      <button
        onClick={() => setIsOpen(true)}
        className={`h-[60px] w-[180px] rounded-full text-white text-[22px] flex items-center justify-center absolute bottom-[-25px] cursor-pointer ${THEME_GRADIENT}`}
      >
        <img src={ShareCodePNG} className="h-[27px] w-[24px] mr-2" />
        Share
      </button>

      {/* SHARE MODAL */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-40">
          <div className="w-[470px] bg-white rounded-2xl p-6 relative shadow-2xl">
            {loading ? (
              <LoadingDots />
            ) : (
              <>
                <span
                  onClick={() => setIsOpen(false)}
                  className="absolute top-3 right-5 text-3xl cursor-pointer text-gray-400"
                >
                  &times;
                </span>

                <div className="flex flex-col items-center justify-center">
                  <h2 className="text-2xl font-bold my-4">Share</h2>

                  {/* INPUT + COPY */}
                  <div className="flex w-[75%] items-center mb-3">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="flex-1 px-3 py-2 rounded-xl bg-gray-100 outline-none border-2 border-transparent focus:border-[#ffc951]"
                    />
                    <div
                      onClick={handleCopy}
                      className="ml-3 cursor-pointer p-1"
                    >
                      <img src={CopyIconPNG} width={25} />
                    </div>
                  </div>

                  <UnderLine width={70} isPercent />

                  <div className="flex justify-around gap-4 mt-4">
                    {/* WHATSAPP - Show Options */}
                    <div
                      className="text-center cursor-pointer"
                      onClick={() => {
                        setIsOpen(false);
                        setWhatsappOptionsOpen(true);
                      }}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <img src={WhatsAppIconPNG} width={28} height={28} />
                      </div>
                    </div>

                    <div
                      onClick={() => handleShare("facebook")}
                      className="cursor-pointer w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    >
                      <img src={FacebookPNG} className="w-10 h-10" />
                    </div>

                    <div
                      onClick={() => handleShare("telegram")}
                      className="cursor-pointer w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    >
                      <img src={TelegramPNG} className="w-10 h-10" />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MULTIPLE INPUT MODAL */}
      {multiModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-4">
          <div className="w-full max-w-lg bg-white rounded-3xl p-8 relative shadow-2xl animate-in">
            {/* Close Button */}
            <button
              onClick={() => setMultiModalOpen(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-9 h-9 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Send QR via WhatsApp</h2>
              <p className="text-gray-500 text-sm mt-2">Add phone numbers to send QR codes</p>
            </div>

            {/* Phone Input Fields */}
            <div className="space-y-3 mb-6 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
              {phoneInputs.map((num, index) => (
                <div
                  key={index}
                  className="flex gap-3 items-center group"
                >
                  <div className="flex-1 relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <input
                      placeholder="Enter phone number with country code"
                      value={num}
                      onChange={(e) => updatePhone(index, e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 border-gray-200 outline-none focus:border-green-400 focus:ring-4 focus:ring-green-50 transition-all text-gray-700"
                    />
                  </div>

                  <button
                    disabled={phoneInputs.length === 1}
                    onClick={() => removePhoneInput(index)}
                    className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-500 hover:bg-red-600 text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-red-500 shadow-sm hover:shadow-md"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {/* Add More Button */}
            <button
              onClick={addPhoneInput}
              className={`w-full py-3.5 rounded-xl text-white font-medium text-[15px] flex items-center justify-center gap-2 transition-all hover:shadow-lg ${THEME_GRADIENT}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Another Number
            </button>

            {/* Send Button */}
            {loading ? <LoadingDots /> : <button
              onClick={handleSendToMultiple}
              disabled={phoneInputs.some(num => !num.trim())}
              className={`mt-4 w-full py-4 rounded-xl text-white font-semibold text-[17px] flex items-center justify-center gap-3 transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed ${THEME_GRADIENT}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Send QR Codes
            </button>}

            {/* Info Text */}
            <p className="text-center text-xs text-gray-400 mt-4">
              QR codes will be sent to all numbers via WhatsApp
            </p>
          </div>

          <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        .animate-in {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
        </div>

      )}

      {whatsappOptionsOpen && (
        <WhatsAppShareOptionsModal
          badge={code ? `Code ${code}` : "Share Code"}
          subtitle="Choose how you want to share"
          onClose={() => {
            setWhatsappOptionsOpen(false);
            setIsOpen(true);
          }}
          loading={loading}
          options={[
            {
              title: "Open WhatsApp",
              description: "Open WhatsApp and share QR code and message",
              icon: <MessageCircle size={22} />,
              showSpinner: true,
              onClick: handleDirectWhatsAppOpen,
            },
            {
              title: "Send to Number",
              description: "Send QR code to specific phone numbers",
              icon: <Phone size={22} />,
              onClick: () => {
                setWhatsappOptionsOpen(false);
                setMultiModalOpen(true);
              },
            },
          ]}
        />
      )}

    </>
  );
};

export default ShareButton;
